// core/exam/simulate.js — ES·1: blueprint-weighted exam draw (pure). Given a tagged MCQ bank, a
// blueprint ({tag: weight}), a question count and a seed → a domain-proportional draw (largest-
// remainder rounding), short domains backfilled from the largest remaining pools, the whole paper
// shuffled deterministically by seed. ZERO FSRS anywhere — a simulation reads the bank, never the
// schedule. (Exam-player shape: Open-Exam-Suite as pattern reference only.)

/** Deterministic PRNG (mulberry32) — seeded, so the same seed always deals the same paper. */
export function rng(seed) {
  let a = seed >>> 0;
  return function () {
    a |= 0; a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const shuffle = (arr, rand) => {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

/** Largest-remainder quotas: integer allocations summing to `count`, proportional to weights. */
export function quotasFor(blueprint, count) {
  const entries = Object.entries(blueprint).filter(([, w]) => w > 0);
  const total = entries.reduce((s, [, w]) => s + w, 0);
  if (!entries.length || total <= 0 || count <= 0) return {};
  const raw = entries.map(([tag, w]) => ({ tag, exact: (count * w) / total }));
  const out = Object.fromEntries(raw.map((r) => [r.tag, Math.floor(r.exact)]));
  let left = count - Object.values(out).reduce((s, n) => s + n, 0);
  const byRemainder = [...raw].sort((a, b) =>
    (b.exact - Math.floor(b.exact)) - (a.exact - Math.floor(a.exact)) || b.exact - a.exact || a.tag.localeCompare(b.tag));
  for (let i = 0; left > 0; i = (i + 1) % byRemainder.length, left--) out[byRemainder[i].tag] += 1;
  return out;
}

/**
 * Deal a simulated exam.
 * @param {{ bank: Array<{id: string, tags?: string[]}>, blueprint: Record<string, number>,
 *           count: number, seed: number }} opts
 * @returns {{ questions: Array<object>, manifest: { seed: number, requested: number, dealt: number,
 *             perDomain: Array<{tag: string, want: number, drawn: number}>, backfilled: number,
 *             shortfall: number } }}
 */
export function simulateDraw({ bank, blueprint, count, seed }) {
  const rand = rng(seed);
  const items = (bank ?? []).filter((q) => q && q.id);
  const quotas = quotasFor(blueprint ?? {}, count);
  const drawnIds = new Set();
  const questions = [];
  const perDomain = [];

  // per-domain draw, blueprint order fixed by tag name for determinism
  for (const tag of Object.keys(quotas).sort()) {
    const want = quotas[tag];
    const pool = shuffle(items.filter((q) => (q.tags ?? []).includes(tag) && !drawnIds.has(q.id)), rand);
    const take = pool.slice(0, want);
    for (const q of take) { drawnIds.add(q.id); questions.push(q); }
    perDomain.push({ tag, want, drawn: take.length });
  }

  // short domains backfill from the LARGEST remaining pools (recorded, never silent)
  let backfilled = 0;
  let remaining = shuffle(items.filter((q) => !drawnIds.has(q.id)), rand);
  while (questions.length < count && remaining.length > 0) {
    // largest pool = the tag with the most remaining items; take one of its items each pass
    const byTag = new Map();
    for (const q of remaining) for (const t of (q.tags?.length ? q.tags : ["(untagged)"])) {
      byTag.set(t, (byTag.get(t) ?? 0) + 1);
    }
    const biggest = [...byTag.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))[0][0];
    const idx = remaining.findIndex((q) => (q.tags?.length ? q.tags : ["(untagged)"]).includes(biggest));
    const q = remaining[idx];
    remaining.splice(idx, 1);
    drawnIds.add(q.id);
    questions.push(q);
    backfilled += 1;
  }

  return {
    questions: shuffle(questions, rand), // exam order never groups by domain
    manifest: {
      seed, requested: count, dealt: questions.length,
      perDomain, backfilled,
      shortfall: Math.max(0, count - questions.length), // the bank simply ran out — honest
    },
  };
}
